import Portal from '@/types/Portal'
import { Col, Form, Placeholder, Row } from 'react-bootstrap'
import { useParams, useNavigate } from 'react-router'
import _ from 'lodash'
import { SubmitHandler, useController, useForm } from 'react-hook-form'
import { useEffect } from 'react'
import LoadingButton from '@/components/shared/LoadingButton'
import FileInput, { FileFormat } from '@/components/FileInput'
import PortalProject from '@/types/PortalProject'
import { useToaster } from '@/components/shared/ToasterProvider'
import Project from '@/types/Project'
import TitledRecordsSelect from '@/components/TitledRecordsSelect'
import { useGetPortalQuery, useGetPortalProjectsQuery, useCreatePortalMutation, useUpdatePortalMutation } from '@/features/apiSliceWithPortals'
import useToastQuery from '@/hooks/useToastQuery'
import useToastMutation from '@/hooks/useToastMutation'

type FormPortal = Required<Pick<Portal, 'title' | 'description' | 'projects'>> & {
  photos: FileList,
}

function buildRequestBody(formPortal: FormPortal, currentPortal?: Required<Portal>) {
  const formData = new FormData()
  formData.append('portal[title]', formPortal.title)
  formData.append('portal[description]', formPortal.description)
  formData.appendFileList<Portal>(formPortal.photos, 'portal', 'photos')

  let portalProjects: Partial<PortalProject>[]
  if (currentPortal) {
    const existingProjectsIds = currentPortal.projects.map(p => p.id)
    const selectedProjectsIds = formPortal.projects?.map(p => p.id)
    const newProjectsIds = selectedProjectsIds?.filter(id => !existingProjectsIds.includes(id))
    const removedProjectsIds = existingProjectsIds.filter(id => !selectedProjectsIds?.includes(id))

    const removedPortalProjects = currentPortal.portalProjects
      .filter(pp => removedProjectsIds.includes(pp.projectId))
      .map(({ id, projectId }) => ({ id, projectId, _destroy: true }))

    portalProjects = [
      ...removedPortalProjects,
      ...newProjectsIds?.map((id) => ({ projectId: id })) ?? [],
    ]
  } else {
    portalProjects = formPortal.projects.map(({ id: projectId }) => ({ projectId: projectId! }))
  }

  portalProjects.forEach((pp, idx) => {
    pp?.id && formData.append(`portal[portal_projects_attributes][${idx}][id]`, String(pp.id))
    formData.append(`portal[portal_projects_attributes][${idx}][project_id]`, String(pp.projectId))
    pp?._destroy && formData.append(`portal[portal_projects_attributes][${idx}][_destroy]`, String(pp._destroy))
  })

  return formData
}

export default function PortalForm() {
  const navigate = useNavigate()
  const { portalSlug } = useParams()
  const {
    data,
    isFetching: isFetchingPortal,
  } = useToastQuery(useGetPortalQuery, portalSlug || '', { skip: !portalSlug })
  const { data: projects, isFetching: isFetchingProjects } = useGetPortalProjectsQuery()
  const [createPortal, { isLoading: isCreateLoading }] = useToastMutation(useCreatePortalMutation)
  const [updatePortal, { isLoading: isUpdateLoading }] = useToastMutation(useUpdatePortalMutation)
  const draftPortal = data as Required<Portal>

  const showToaster = useToaster()
  const action = portalSlug ? 'update' : 'create'
  const { register, handleSubmit, control, setValue } = useForm<FormPortal>({
    defaultValues: {
      projects: [],
    },
  })
  const { field: { ref: _ref, ...projectsField } } = useController({
    name: 'projects',
    control,
    rules: { validate: value => Array.isArray(value) && value.length > 0 },
  })

  useEffect(() => {
    if (!draftPortal) return

    setValue('title', draftPortal.title)
    setValue('description', draftPortal.description)
    setValue('projects', draftPortal.projects ?? [])
  }, [draftPortal])

  const onSubmit: SubmitHandler<FormPortal> = async (formPortal) => {
    const body = buildRequestBody(formPortal, draftPortal)
    const { data: portal } = await (portalSlug ? updatePortal({ portalSlug, body }) : createPortal(body))
    navigate(`/${portal?.slug}/`)
    showToaster({ message: `Portal ${formPortal.title} ${action}d successfully!` })
  }

  if (isFetchingPortal || isFetchingProjects) return <PortalFormSkeleton />

  return (
    <Row className={'justify-content-center mt-4'}>
      <Col sm={12} md={10} lg={8}>
        <h3 className="mb-4">{`${_.capitalize(action)} portal`}</h3>
        <Form
          className="d-flex flex-column gap-3"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Form.Group>
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              {...register('title', { required: true })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>
              Description <span className="text-muted">(Markdown supported)</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              {...register('description', { required: true })}
            />
          </Form.Group>
          <FileInput
            title="Photos"
            multiple
            accept={[FileFormat.image]}
            {...register('photos')}
            attachments={draftPortal?.photos}
          />

          {projects && <TitledRecordsSelect<Project>
            {...projectsField}
            options={projects}
            isMulti
            withAll
          />}
          <LoadingButton
            type="submit"
            loading={isCreateLoading || isUpdateLoading}
            text={_.capitalize(action)}
          />
        </Form>
      </Col>
    </Row>
  )
}

function PortalFormSkeleton() {
  return (
    <Row className="justify-content-center mt-4">
      <Col sm={12} md={10} lg={8} className="d-flex flex-column gap-3">
        <Placeholder as="h3" animation="glow"><Placeholder xs={3} /></Placeholder>
        <div>
          <Placeholder as="p" animation="glow" className="mb-1"><Placeholder xs={2} /></Placeholder>
          <Placeholder animation="glow"><Placeholder xs={12}
                                                     style={{ height: 38, borderRadius: 4 }} /></Placeholder>
        </div>
        <div>
          <Placeholder as="p" animation="glow" className="mb-1"><Placeholder xs={2} /></Placeholder>
          <Placeholder animation="glow"><Placeholder xs={12}
                                                     style={{ height: 120, borderRadius: 4 }} /></Placeholder>
        </div>
        <div>
          <Placeholder as="p" animation="glow" className="mb-1"><Placeholder xs={2} /></Placeholder>
          <Placeholder animation="glow"><Placeholder xs={12}
                                                     style={{ height: 38, borderRadius: 4 }} /></Placeholder>
        </div>
      </Col>
    </Row>
  )
}
